/* eslint-disable no-unused-vars */
import { getAttachmentByReference } from '../../utils/mongoUtils'
import { openAttachment, launchPDFViewer, setMarkupFileKind } from '../../scripts/js/forgeviewer'
import { getDocumentVersions } from '@/features/Documents/utils/viewerVersionNotice'
import { DocHubDocument, ForgeAttachment, ForgePageMarkup } from '../../scripts/js/forgeModels'
import { GET_DOCUMENTS_BY_QUERY, GET_DOCUMENT_TYPES, DELETE_DOCUMENTS, UPDATE_DOCUMENT, GET_ALL_DOCUMENTS } from '../../utils/attachmentsAPI'
import store from '../index'
import HttpService from '@/utils/httpService'
import Vue from 'vue'
import { log as debugLog, error as debugError } from '@/utils/debugLogger';
import { FACILITY_REQUEST } from '@/utils/http/RequestRegistry';
const THREE = window.THREE;

/**
 * File name of one version of a document (`revisionId` null: the current version), from the
 * shared versions cache. Null when unknown; callers then fall back to the name they have.
 */
async function versionFileName(documentId, revisionId) {
    if (!documentId) return null;
    try {
        const versions = await getDocumentVersions(documentId);
        const version = revisionId ? versions.find((v) => v.id === revisionId) : versions.find((v) => v.isCurrent);
        return version?.fileName || null;
    } catch (err) {
        debugError('[Attachments]', 'Could not read the version file name:', err);
        return null;
    }
}

export const Attachments = {
    state: {
        attachments: [],
        showUpdateDocumentPopup: false,
        predefinedDocumentInfo: null,
        documentTypes: [],
        documentsIdsToUpdate: [],
        officeDocumentUrl: '',
        defaultDocumentType: 'Specifications',
        /** @type {'Facility' | 'FacilityGroup'} Upload scope type for new documents */
        uploadScopeType: 'Facility',
        /** @type {string|null} Facility group ID when uploading group-scoped documents */
        uploadFacilityGroupId: null,
        /** @type {Object|null} Banner over the PDF viewer when it shows, or its markups belong to, a non-current version */
        documentVersionNotice: null,
    },
    mutations: {
        SET_ALL_ATTACHMENTS(state, value) {
            state.attachments = value;
        },
        SET_SHOW_UPDATE_DOCUMENT_POPUP(state, value) {
            state.showUpdateDocumentPopup = value;
        },
        SET_PREDEFINED_DOCUMENT_INFO(state, value) {
            state.predefinedDocumentInfo = value;
        },
        SET_DOCUMENTS_TYPES(state, value) {
            state.documentTypes = value;
        },
        SET_DOCUMENTS_IDS_TO_UPDATE(state, value) {
            state.documentsIdsToUpdate = value;
        },
        SET_OFFICE_DOCUMENT_URL(state, value) {
            state.officeDocumentUrl = value;
        },
        SET_DEFAULT_DOCUMENT_TYPE(state, value) {
            state.defaultDocumentType = value;
        },
        SET_UPLOAD_SCOPE_TYPE(state, value) {
            state.uploadScopeType = value;
        },
        SET_UPLOAD_FACILITY_GROUP_ID(state, value) {
            state.uploadFacilityGroupId = value;
        },
        SET_DOCUMENT_VERSION_NOTICE(state, value) {
            state.documentVersionNotice = value;
        }
    },
    getters: {
        GET_ALL_ATTACHMENTS(state) {
            return state.attachments;
        },
        GET_SHOW_UPDATE_DOCUMENT_POPUP(state) {
            return state.showUpdateDocumentPopup;
        },
        GET_DOCUMENT_VERSION_NOTICE(state) {
            return state.documentVersionNotice;
        },
        GET_PREDEFINED_DOCUMENT_INFO(state) {
            return state.predefinedDocumentInfo;
        },
        GET_DOCUMENT_TYPES(state) {
            return state.documentTypes;
        },
        GET_DOCUMENTS_IDS_TO_UPDATE(state) {
            return state.documentsIdsToUpdate;
        },
        GET_P_IDS(state) {
            // Read through a known array: this getter is evaluated on every mount that touches
            // P&IDs, long before (or after a failed) REQUEST_DOCUMENT_TYPES. With no type list
            // there is no P&ID type to match, which is an empty result, not a crash.
            const types = Array.isArray(state.documentTypes) ? state.documentTypes : [];
            var typeId=types.find(e=>e.name=='P&IDs')?.id;
            return state.attachments.filter(a => a.documentTypeId==typeId).sort((a, b) => a.documentNumber.localeCompare(b.documentNumber)) ?? [];
        },
        GET_OFFICE_DOCUMENT_URL(state) {
            return state.officeDocumentUrl;
        },
        GET_DEFAULT_DOCUMENT_TYPE(state) {
            return state.defaultDocumentType;
        },
        GET_DEFAULT_DOCUMENT_TYPE_ID(state) {
            // Same reason as GET_P_IDS: no usable type list means the default type has no id yet,
            // which every caller already handles as undefined.
            if (!Array.isArray(state.documentTypes)) return undefined;
            return state.documentTypes.find(t => t.name === state.defaultDocumentType)?.id;
        },
        GET_UPLOAD_SCOPE_TYPE(state) {
            return state.uploadScopeType;
        },
        GET_UPLOAD_FACILITY_GROUP_ID(state) {
            return state.uploadFacilityGroupId;
        }
    },
    actions: {
        UPDATE_DOCUMENTS({ dispatch, commit, state }, { attachments }) {
            state.attachments = attachments.map(d => {
                // Exclude scopeType from API response - it's a computed getter on DocHubDocument
                const { scopeType, ...documentData } = d;
                return Object.assign(new DocHubDocument(), documentData);
            });
        },
        async FETCH_ALL_DOCUMENTS({ dispatch, commit, state }) {
            return GET_ALL_DOCUMENTS({
                assetId: store.state.assetId,
                Page: -1,
                ItemsPerPage: -1,
            }, FACILITY_REQUEST)
                .then(({ items }) => {
                    dispatch('UPDATE_DOCUMENTS', { attachments: items });
                    //store.dispatch('RequestPIdsLinkedTags');
                })
                .catch((error) => {
                    debugLog(error);
                    Vue.prototype.$dialog.notify.error("Error fetching documents. Please try again later.");
                });
        },
        REQUEST_DOCUMENT_TYPES({ dispatch, commit, state }) {
            return GET_DOCUMENT_TYPES(FACILITY_REQUEST)
                .then(types => {
                    // The endpoint answers with a bare list, but this promise resolves on more
                    // than the happy path - a retried request, a proxied response, or the SPA
                    // host answering /api/* with index.html all hand back something that is not
                    // an array. Every reader of documentTypes calls .find on it, so a non-array
                    // is stored as nothing at all, and the shape is logged so the next
                    // occurrence names its own source.
                    if (!Array.isArray(types)) {
                        debugError('REQUEST_DOCUMENT_TYPES: expected an array of document types', {
                            type: typeof types,
                            // Objects are described by their keys and primitives by their head:
                            // stringifying an arbitrary object here could throw inside the guard.
                            keys: types && typeof types === 'object' ? Object.keys(types).slice(0, 10) : null,
                            preview: typeof types === 'object' ? null : String(types).slice(0, 200),
                        });
                        commit('SET_DOCUMENTS_TYPES', []);
                        return;
                    }
                    commit('SET_DOCUMENTS_TYPES', types);
                })
                .catch(error => {
                    debugLog(error);
                })
        },
        REQUEST_UPDATE_DOCUMENT({ dispatch, commit, state }, document) {
            return new Promise((resolve, reject) => {
                UPDATE_DOCUMENT(document)
                    .then(a => {
                        // Exclude scopeType from API response - it's a computed getter on DocHubDocument
                        const { scopeType, ...documentData } = a;
                        let u = Object.assign(new DocHubDocument(), documentData);
                        state.attachments = [...state.attachments.filter(doc => doc.documentId != u.documentId), u];
                        resolve(a);
                    })
                    .catch(reject);
            })
        },
        UPDATE_UPLOADED_DOCUMENTS({ dispatch, commit, state }, { ids }) {
            state.documentsIdsToUpdate = [...ids];
            state.showUpdateDocumentPopup = true;
        },
        GET_DOCUMENTS_TO_UPDATE({ dispatch, commit, state }, payload) {
            return new Promise((resolve, reject) => {
                let query = state.documentsIdsToUpdate.reduce((p, c) => {
                    return p + `documentIds=${c}&`
                }, "").slice(0, -1);

                GET_DOCUMENTS_BY_QUERY(query).then(resolve).catch(reject);
            })
        },
        async CANCEL_UPDATE_POPUP({ dispatch, commit, state }, payload) {
            return DELETE_DOCUMENTS(state.documentsIdsToUpdate).then(_ => {
                state.predefinedDocumentInfo = null;
                state.showUpdateDocumentPopup = false;
            });
        },
        HIDE_UPDATE_DOCUMENTS_POPUP({ dispatch, commit, state }, payload) {
            state.showUpdateDocumentPopup = false;
            state.predefinedDocumentInfo = null;
        },
        DELETE_ATTACHMENTS_FROM_DATA({ dispatch, commit, state }, { ids }) {
            state.attachments = state.attachments.filter(a => !ids.includes(a.documentId));
        },
        /**
         * The markups attachment of a document, for the viewer. Links are per version: `revisionId`
         * gives a version, else the current version's are loaded. A version without markups yet gets
         * an empty attachment stamped with that version, so what is drawn on it is saved there.
         */
        GET_DOCUMENT_BY_REFERENCE_ID({ dispatch, commit, state }, { documentId, storageReference, name, revisionId = null }) {
            return new Promise((resolve, reject) => {
                getAttachmentByReference(documentId, { revisionId }).then((attachment) => {
                    if (!attachment.id && revisionId) attachment.documentRevisionId = revisionId;
                    // URLs are now pre-signed by the backend
                    let url = `${storageReference}`;
                    attachment.url = decodeURI(url);
                    attachment.name = name;
                    var forgeAttachment = Object.assign(new ForgeAttachment(), attachment);
                    // Ensure document ID is set for proxy URL (used by getPdfProxyUrl in forgeviewer.js)
                    forgeAttachment.referenceDocumentId = documentId;
                    if (!forgeAttachment.pagesMarkups) forgeAttachment.pagesMarkups = [];
                    forgeAttachment.pagesMarkups = forgeAttachment.pagesMarkups.map(e => Object.assign(new ForgePageMarkup(), e));
                    forgeAttachment.pagesMarkups.forEach(m => {
                        if (m.cropBox)
                            m.cropBox = Object.assign(new THREE.Box3(m.cropBox.min, m.cropBox.max));
                    })
                    resolve(forgeAttachment);
                }).catch(reject);
            })
        },
        async OPEN_ATTACHMENT_IN_VIEWER({ dispatch }, _attachment) {
            const revisionId = _attachment.viewRevisionId || null;
            // A version's own file name: versions of one document can be different file types.
            const name = revisionId
                ? _attachment.viewFileName || (await versionFileName(_attachment.documentId, revisionId)) || _attachment.fileName
                : _attachment.fileName;
            const attachment = await dispatch('GET_DOCUMENT_BY_REFERENCE_ID', {
                documentId: _attachment.documentId,
                storageReference: _attachment.storageReference,
                name,
                // An older version opens with its own markups and links.
                revisionId,
            });
            _attachment.url = attachment.url;
            // Optional: open an older version (viewRevisionId) from its own file URL.
            if (revisionId) {
                attachment.viewRevisionId = revisionId;
                attachment.viewStorageReference = _attachment.viewStorageReference || null;
            }
            openAttachment(attachment);
            return attachment;
        },
        /**
         * Shows another version of the document open in the doc view, with that version's own
         * markups and links (links are per version, so they are loaded again, not carried over).
         * `revisionId` null shows the current version.
         */
        async OPEN_DOCUMENT_VERSION_IN_VIEWER(
            { dispatch, commit, rootGetters },
            { revisionId = null, storageReference = null, fileName = null } = {},
        ) {
            const active = rootGetters.getActiveAttachment;
            const documentId = active?.referenceDocumentId;
            if (!documentId) return null;

            const attachment = await dispatch('GET_DOCUMENT_BY_REFERENCE_ID', {
                documentId,
                storageReference: active.url,
                // The version's own file name: v1 can be a PDF and v2 an image.
                name: fileName || (await versionFileName(documentId, revisionId)) || active.name,
                revisionId,
            });
            // The document's own file URL, as opened: GET_DOCUMENT_BY_REFERENCE_ID decodes it again.
            attachment.url = active.url;
            if (revisionId) {
                attachment.viewRevisionId = revisionId;
                attachment.viewStorageReference = storageReference || null;
            }
            // PDF or image: decided per version. Anything else goes to the viewer for its type.
            if (!setMarkupFileKind(attachment.name)) {
                openAttachment(attachment);
                return attachment;
            }
            commit('setActiveAttachment', attachment);
            await launchPDFViewer(attachment);
            return attachment;
        },
        UPDATE_P_ID_TAGS({ dispatch, commit, state }, { documentId, tags }) {
            return new Promise((resolve, reject) => {
                GET_DOCUMENTS_BY_QUERY(`documentIds=${documentId}`)
                    .then((list) => {
                        let document = list[0];

                        document.forgeTagsIds = [... new Set(tags)];

                        dispatch('REQUEST_UPDATE_DOCUMENT', document);
                    })
                    .catch(reject);
            })
        },
        RequestPIdsLinkedTags({ dispatch, commit, state }) {
            let pId = store.getters.GET_DOCUMENT_TYPES.find(t => t.name === 'P&IDs')?.id;

            if (!pId) return;

            let pIds = state.attachments.filter(a => a.documentTypeId === pId);

            if (!pIds.length) return;

            let documentsIds = pIds.map(p => p.documentId);

            if (!documentsIds.length) return;

            HttpService.post({
                url: 'api/attachments/pIds/linkedTags',
                data: documentsIds
            }).then((response) => {
                let data = response.data;

                const pIds = store.getters.GET_P_IDS;

                if (!data?.length) return;

                data.forEach(document => {
                    let pIdIndex = pIds.findIndex(p => p.documentId == document.documentId);

                    if (pIdIndex < 0) return;

                    let ids = [];

                    document.links.forEach(link => {
                        // let metadata = JSON.parse(link.metadata);

                        // let tagId = metadata?.id;

                        if (!link.tagId) return;

                        ids.push(link.tagId);
                    });

                    pIds[pIdIndex].forgeTagsIds = [... new Set([...(pIds[pIdIndex].originalLinkedForgeTagsIds ?? []), ...ids])];
                });
            }).catch((error) => {
                debugLog(error);
            });
        }
    },
}
